import { bound } from "@frui.ts/helpers";
import { ScreenBase } from "@frui.ts/screens";
import { validate } from "@frui.ts/validation";
import { attachAutomaticValidator } from "@frui.ts/validation/dist";
import type CustomerRefundCandidateDetailResponse from "entities/customerRefundCandidateDetailResponse";
import CustomerRefundCandidateRejectionRequest, {
  CustomerRefundCandidateRejectionRequestValidationRules,
} from "entities/customerRefundCandidateRejectionRequest";
import type CustomerRefundCandidateResponse from "entities/customerRefundCandidateResponse";
import type { interfaces } from "inversify";
import { observable } from "mobx";
import CustomerRefundCandidatesRepository from "repositories/customerRefundCandidatesRepository";
import EventBus, { customerRefundCandidateRejected } from "services/eventBus";
import LocalizationService from "services/localizationService";
import NotificationService, { NotificationType } from "services/notificationService";
import AlertsViewModel from "viewModels/alertsViewModel";
import { Modules } from "../../../../config";

export default class CandidateRejectionViewModel extends ScreenBase {
  @observable rejection: CustomerRefundCandidateRejectionRequest;

  constructor(
    private item: CustomerRefundCandidateResponse | CustomerRefundCandidateDetailResponse,
    private repository: CustomerRefundCandidatesRepository,
    private localizationService: LocalizationService,
    private notificationService: NotificationService,
    public alertsViewModel: AlertsViewModel,
    private eventBus: EventBus
  ) {
    super();
    this.rejection = new CustomerRefundCandidateRejectionRequest();
    attachAutomaticValidator(this.rejection, CustomerRefundCandidateRejectionRequestValidationRules);
  }

  @bound
  async onRejectSubmit() {
    if (validate(this.rejection)) {
      try {
        await this.repository.rejection(this.item.id, this.rejection);
        this.eventBus.publish(customerRefundCandidateRejected({ customerRefundCandidate: this.item }));
        this.notificationService.add({ title: this.translate("rejected"), type: NotificationType.SUCCESS });
        this.requestClose();
      } catch (e) {
        // Silent
      }
    }
  }

  translate(code: string) {
    return this.localizationService.translateModule(Modules.finance, `customerRefundCandidates.rejection.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (item: CustomerRefundCandidateResponse | CustomerRefundCandidateDetailResponse) => {
      return new CandidateRejectionViewModel(
        item,
        container.get(CustomerRefundCandidatesRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(AlertsViewModel),
        container.get(EventBus)
      );
    };
  }
}
